const requirePositiveInteger = (value, label) => {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 1) {
    throw new Error(`${label} must be a positive whole number`);
  }
  return parsed;
};

const pluralizeUnit = (name, quantity) => {
  if (quantity === 1) return name;
  return /s$/i.test(name) ? name : `${name}s`;
};

export function shouldRequireParentBarcode(editingItem) {
  return Boolean(editingItem && !editingItem.unit_inventory_enabled);
}

export function getParentBarcodeForSave(editingItem, barcode) {
  return editingItem?.unit_inventory_enabled
    ? ""
    : String(barcode || "").trim();
}

export function getBaseUnitDisplayName(units, quantity = 2) {
  const baseUnit = units.find(
    (unit) => unit.is_base_unit && unit.is_active !== false,
  );
  const name = String(baseUnit?.unit_name || "").trim();
  if (!name) return quantity === 1 ? "base unit" : "base units";
  return pluralizeUnit(name, quantity);
}

export function createUnitDraft(overrides = {}) {
  return {
    id: overrides.id,
    unit_name: overrides.unit_name ?? "",
    unit_code: overrides.unit_code ?? "",
    conversion_factor: overrides.is_base_unit ? 1 : (overrides.conversion_factor ?? 1),
    selling_price: overrides.selling_price ?? "",
    barcode: overrides.barcode ?? "",
    barcode_source: overrides.barcode_source ?? "manufacturer",
    is_base_unit: Boolean(overrides.is_base_unit),
    can_sell: overrides.can_sell ?? true,
    can_purchase: overrides.can_purchase ?? false,
    is_active: overrides.is_active ?? true,
    barcode_is_shared: Boolean(overrides.barcode_is_shared),
    shared_barcode_count: Number(overrides.shared_barcode_count) || 0,
  };
}

const normalizeBarcode = (barcode) =>
  String(barcode || "").trim().toLocaleLowerCase();

export function findSharedUnitBarcodeConflicts(
  units,
  inventoryItems,
  currentItemId,
) {
  const currentItem = (inventoryItems || []).find(
    (item) => String(item.id) === String(currentItemId),
  );
  const originalUnitsById = new Map(
    (currentItem?.product_units || []).map((unit) => [String(unit.id), unit]),
  );
  const externalUnitsByBarcode = new Map();

  (inventoryItems || []).forEach((item) => {
    if (String(item.id) === String(currentItemId)) return;
    (item.product_units || []).forEach((unit) => {
      if (unit.is_active === false) return;
      const barcode = normalizeBarcode(unit.barcode);
      if (!barcode) return;
      const matches = externalUnitsByBarcode.get(barcode) || [];
      matches.push(unit);
      externalUnitsByBarcode.set(barcode, matches);
    });
  });

  const conflictsByBarcode = new Map();
  (units || []).forEach((unit, unitIndex) => {
    if (unit.is_active === false) return;
    const barcode = normalizeBarcode(unit.barcode);
    const matches = externalUnitsByBarcode.get(barcode) || [];
    if (!barcode || matches.length === 0) return;

    const originalUnit = unit.id
      ? originalUnitsById.get(String(unit.id))
      : null;
    const isUnchangedSharedBarcode = Boolean(
      originalUnit?.barcode_is_shared &&
      normalizeBarcode(originalUnit.barcode) === barcode,
    );
    if (isUnchangedSharedBarcode) return;

    const conflict = conflictsByBarcode.get(barcode) || {
      barcode,
      matchCount: matches.length,
      unitIndexes: [],
    };
    conflict.unitIndexes.push(unitIndex);
    conflictsByBarcode.set(barcode, conflict);
  });

  return Array.from(conflictsByBarcode.values());
}

export function approveSharedUnitBarcodes(units, conflicts) {
  const approvedIndexes = new Set(
    (conflicts || []).flatMap((conflict) => conflict.unitIndexes || []),
  );
  return (units || []).map((unit, index) =>
    approvedIndexes.has(index)
      ? {
          ...unit,
          barcode_is_shared: true,
          allow_shared_barcode: true,
        }
      : unit,
  );
}

export function normalizeProductUnits(units) {
  return units.map((unit, index) => ({
    ...createUnitDraft(unit),
    unit_name: String(unit.unit_name || "").trim(),
    unit_code: String(unit.unit_code || unit.unit_name || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 24),
    conversion_factor: unit.is_base_unit ? 1 : Number(unit.conversion_factor),
    selling_price: Number(unit.selling_price),
    barcode: String(unit.barcode || "").trim(),
    display_order: index,
  }));
}

export function validateProductUnits(units) {
  const errors = {};
  const rows = {};
  const activeUnits = units.filter((unit) => unit.is_active !== false);
  const baseUnits = activeUnits.filter((unit) => unit.is_base_unit);

  if (baseUnits.length !== 1) {
    errors.base = "Choose exactly one base unit.";
  } else if (Number(baseUnits[0].conversion_factor) !== 1) {
    errors.base = "The base unit conversion factor must be 1.";
  }

  activeUnits.forEach((unit, index) => {
    const rowErrors = [];
    if (!String(unit.unit_name || "").trim()) rowErrors.push("Enter a unit name.");
    const factor = Number(unit.conversion_factor);
    if (!Number.isSafeInteger(factor) || factor < 1) {
      rowErrors.push("Conversion factor must be a positive whole number.");
    }
    if (unit.selling_price === "" || !Number.isFinite(Number(unit.selling_price)) ||
        Number(unit.selling_price) < 0) {
      rowErrors.push("Selling price must be zero or greater.");
    }
    if (rowErrors.length) rows[index] = rowErrors;
  });
  if (Object.keys(rows).length) errors.rows = rows;

  const names = activeUnits
    .map((unit) => String(unit.unit_name || "").trim().toLocaleLowerCase())
    .filter(Boolean);
  if (new Set(names).size !== names.length) {
    errors.names = "Unit names must be unique.";
  }

  const barcodes = activeUnits
    .map((unit) => String(unit.barcode || "").trim().toLocaleLowerCase())
    .filter(Boolean);
  if (new Set(barcodes).size !== barcodes.length) {
    errors.barcodes = "Unit barcodes must be unique.";
  }
  return errors;
}

export function buildUnitConversionPreview(units) {
  const active = normalizeProductUnits(units)
    .filter((unit) => unit.is_active !== false && unit.unit_name &&
      Number.isSafeInteger(unit.conversion_factor) && unit.conversion_factor > 0)
    .sort((left, right) => left.conversion_factor - right.conversion_factor);
  const base = active.find((unit) => unit.is_base_unit);
  if (!base) return [];

  return active
    .filter((unit) => !unit.is_base_unit)
    .map((unit) => {
      const intermediate = [...active]
        .reverse()
        .find((candidate) =>
          candidate.conversion_factor < unit.conversion_factor &&
          unit.conversion_factor % candidate.conversion_factor === 0);
      const baseText =
        `${unit.conversion_factor} ${pluralizeUnit(base.unit_name, unit.conversion_factor)}`;
      if (!intermediate || intermediate.is_base_unit) {
        return `1 ${unit.unit_name} = ${baseText}`;
      }
      const intermediateQuantity =
        unit.conversion_factor / intermediate.conversion_factor;
      return `1 ${unit.unit_name} = ${intermediateQuantity} ` +
        `${pluralizeUnit(intermediate.unit_name, intermediateQuantity)} = ${baseText}`;
    });
}

export function previewBaseStockConversion(currentStock, interpretationFactor) {
  const stock = Number(currentStock);
  if (!Number.isSafeInteger(stock) || stock < 0) {
    throw new Error("Current stock must be a nonnegative whole number");
  }
  const factor = requirePositiveInteger(interpretationFactor, "Conversion factor");
  const resultingBaseStock = stock * factor;
  if (!Number.isSafeInteger(resultingBaseStock)) {
    throw new Error("Converted stock is too large");
  }
  return {
    currentStock: stock,
    interpretationFactor: factor,
    resultingBaseStock,
  };
}

export function calculateUnitAvailability(baseStock, conversionFactor) {
  const stock = Number(baseStock);
  const factor = requirePositiveInteger(conversionFactor, "Conversion factor");
  if (!Number.isFinite(stock)) {
    return { availableUnits: 0, looseBaseUnits: 0 };
  }
  const nonnegativeStock = Math.max(0, Math.trunc(stock));
  return {
    availableUnits: Math.floor(nonnegativeStock / factor),
    looseBaseUnits: nonnegativeStock % factor,
  };
}

export function formatCheckoutWarning(warning) {
  const unit = warning.unit ? ` (${warning.unit})` : "";
  return `${warning.product}${unit}: needed ${warning.requiredDeduction}, recorded ${warning.stockBefore}; stock is now ${warning.stockAfter} (short by ${warning.shortageAmount}).`;
}

export function getTransactionUnitSnapshot(row) {
  return {
    unitName: row.unit_name || "Unit",
    unitQuantity: row.unit_quantity ?? row.quantity,
    conversionFactor: row.conversion_factor ?? 1,
    baseQuantity: row.base_quantity ?? row.quantity,
  };
}
